import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ApplicationFacade,
    CardsNgrxFacade,
    CustomerCard,
    CustomerFacade,
    RelatedCardFacade
} from '@ridango/components';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AddCardButton, AppStartupService, BaseComponent, CardType } from '@ridango/core';
import { switchMap, takeUntil, take, map } from 'rxjs/operators';
import { BankCardsAddEditComponent } from '../bank-cards-add-edit-page/bank-cards-add-edit.component';
import Env from '@ridango/core/env';
import { TermsDialogMobileComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog-mobile/terms-dialog-mobile.component';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest } from 'rxjs';

@Component({
    template: ``
})
export class CardsAddEditBaseComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('bankCardsComponentRef') bankCardsComponent: BankCardsAddEditComponent;
    cardForm: FormGroup;
    public cardType: CardType = CardType.TRAVEL_CARD;
    public CardTypes = CardType;
    assetsPath$ = this.applicationFacade.assetsPath$;
    mode: 'add' | 'edit';
    isMobileView = window.innerWidth < 768;
    initialWindowWidth = window.innerWidth;
    addCardButton: AddCardButton = { disabled: true };
    currency: string;
    bankCardPersonalizationEnabled: boolean;
    card: CustomerCard;
    _bankCardTermsAgreed = false;
    _bankCardTerms = true;
    bankCardTermsForm: FormGroup;
    private _bankCardTermsAgreed$ = new BehaviorSubject<boolean>(false);
    private _addCardButton$ = new BehaviorSubject<AddCardButton>({ disabled: true });
    private cardType$ = new BehaviorSubject<CardType>(CardType.TRAVEL_CARD);

    isBankCardButtonDisabled$ = combineLatest([this._addCardButton$, this._bankCardTermsAgreed$, this.cardType$]).pipe(
        map(([addCardButton, termsAgreed, cardType]) => {
            if (cardType === CardType.BANK_CARD && this._bankCardTerms) {
                return addCardButton.disabled || !termsAgreed;
            }
            return addCardButton.disabled;
        })
    );

    @HostListener('window:resize', ['$event'])
    onResize() {
        if (this.initialWindowWidth !== window.innerWidth) {
            this.initialWindowWidth = window.innerWidth;
            this.isMobileView = window.innerWidth < 768;
        }
    }

    constructor(
        protected applicationFacade: ApplicationFacade,
        protected router: Router,
        protected relatedCardFacade: RelatedCardFacade,
        protected translate: TranslateService,
        private appStartupService: AppStartupService,
        @Inject('ENV') public env: Env,
        private customerFacade: CustomerFacade,
        private cardsFacade: CardsNgrxFacade,
        private matDialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {
        this.customerFacade.getCustomerProfile();
        this.customerFacade.profile$.pipe(takeUntil(this.componentDestroyed$)).subscribe((customer) => {
            this.bankCardPersonalizationEnabled =
                customer?.authenticated && this.env.ACCOUNT?.BANK_CARD_PERSONALIZATION_ENABLED;
        });
        this.relatedCardFacade.resetRelatedCardStatus();
        this.cardsFacade.fetchCustomerCards();
        this.relatedCardFacade.selectedCard$
            .pipe(
                takeUntil(this.componentDestroyed$),
                switchMap((relatedCard) => {
                    return this.cardsFacade.getCardByRelatedCardId(relatedCard?.id);
                })
            )
            .subscribe((card) => (this.card = card));
        this.currency = this.appStartupService.getCurrency();

        this.bankCardTermsForm = new FormGroup({
            bankCardTerms: new FormControl(this._bankCardTermsAgreed, Validators.required)
        });
    }

    addBankCard() {
        if (this.bankCardsComponent) {
            this.bankCardsComponent.addCard();
        }
    }

    navigateByViewMode() {
        if (this.mode === 'add') {
            this.router.navigate([this.translate.currentLang, 'account', 'cards']);
        } else {
            this.router.navigate([this.translate.currentLang, 'account', 'cards', this.card?.id, 'details']);
        }
    }

    toggleBankCardTerms() {
        this._bankCardTermsAgreed = !this._bankCardTermsAgreed;
        this.bankCardTermsForm.controls['bankCardTerms'].patchValue(this._bankCardTermsAgreed);
        this._bankCardTermsAgreed$.next(this._bankCardTermsAgreed);
    }

    agreeBankCardTerms(agreement: boolean) {
        if (agreement) {
            this._bankCardTermsAgreed = true;
            this.bankCardTermsForm.controls['bankCardTerms'].patchValue(true);
            this._bankCardTermsAgreed$.next(true);
        }
    }

    onAddCardButtonChange(newAddCardButton: AddCardButton) {
        this.addCardButton = newAddCardButton;
        this._addCardButton$.next(newAddCardButton);
    }

    onCardTypeChange(newCardType: CardType) {
        this.cardType = newCardType;
        this.cardType$.next(newCardType);
    }

    openBankCardTermsModal(): void {
        const content = this.translate.instant('BANK_CARD_TERMS_CONTENT');
        const template = this.translate.instant('BANK_CARD_TERMS_HTML_TEMPLATE');
        const htmlContent = template.replace('${content}', content);

        const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(htmlContent);

        const termsDialog = this.matDialog.open(TermsDialogMobileComponent, {
            data: {
                termsUrl: dataUrl,
                withAgreeButton: true
            },
            autoFocus: true,
            width: '90%',
            maxWidth: '80vh',
            panelClass: 'terms-dialog-mobile-wrapper'
        });

        termsDialog
            .afterClosed()
            .pipe(take(1))
            .subscribe((termsAgreed) => this.agreeBankCardTerms(termsAgreed));
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this._bankCardTermsAgreed$.complete();
        this._addCardButton$.complete();
    }
}
